/**
 * Campaign store (Zustand). Wraps `campaignService`. Mutating actions update the
 * in-memory list from the DTO the server returns so the UI stays consistent
 * without a full refetch; `sync` re-fetches because it can create many rows.
 */
import { create } from 'zustand';
import * as campaignService from '@/services/campaignService';
import type { CampaignListParams } from '@/services/campaignService';
import { ApiRequestError } from '@/utils/api';
import type {
  CampaignDTO,
  CampaignListResponse,
  CopyCampaignInput,
  SyncInsightsResult,
  SyncResult,
  UpdateBudgetInput,
} from '@shared/types';

type CampaignTotals = CampaignListResponse['totals'];

const EMPTY_TOTALS: CampaignTotals = {
  count: 0,
  running: 0,
  totalDailyBudget: 0,
  totalSpend: 0,
  totalConversions: 0,
  totalRevenue: 0,
};

/** Campaign store state + actions. */
export interface CampaignState {
  campaigns: CampaignDTO[];
  totals: CampaignTotals;
  loading: boolean;
  error: string | null;
  lastParams: CampaignListParams | undefined;
  fetch: (params?: CampaignListParams) => Promise<void>;
  sync: (adAccountId: string) => Promise<SyncResult>;
  syncInsights: (adAccountId?: string) => Promise<SyncInsightsResult>;
  copy: (id: string, input: CopyCampaignInput) => Promise<CampaignDTO>;
  updateStatus: (id: string, status: 'ACTIVE' | 'PAUSED') => Promise<CampaignDTO>;
  updateBudget: (id: string, input: UpdateBudgetInput) => Promise<CampaignDTO>;
  clearError: () => void;
}

/** Extract a user-facing message from an unknown thrown value. */
function toMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export const useCampaignStore = create<CampaignState>()((set, get) => ({
  campaigns: [],
  totals: EMPTY_TOTALS,
  loading: false,
  error: null,
  lastParams: undefined,

  fetch: async (params) => {
    set({ loading: true, error: null, lastParams: params });
    try {
      const { campaigns, totals } = await campaignService.list(params);
      set({ campaigns, totals, loading: false });
    } catch (err) {
      set({ loading: false, error: toMessage(err, 'Không thể tải chiến dịch') });
      throw err;
    }
  },

  sync: async (adAccountId) => {
    set({ loading: true, error: null });
    try {
      const result = await campaignService.sync(adAccountId);
      // Refresh the list (sync may create/update many rows).
      const { campaigns, totals } = await campaignService.list(get().lastParams ?? { adAccountId });
      set({ campaigns, totals, loading: false });
      return result;
    } catch (err) {
      set({ loading: false, error: toMessage(err, 'Đồng bộ chiến dịch thất bại') });
      throw err;
    }
  },

  syncInsights: async (adAccountId) => {
    set({ loading: true, error: null });
    try {
      const result = await campaignService.syncInsights(adAccountId);
      const { campaigns, totals } = await campaignService.list(get().lastParams);
      set({ campaigns, totals, loading: false });
      return result;
    } catch (err) {
      set({ loading: false, error: toMessage(err, 'Cập nhật chỉ số thất bại') });
      throw err;
    }
  },

  copy: async (id, input) => {
    set({ error: null });
    try {
      const created = await campaignService.copy(id, input);
      set((state) => ({ campaigns: [created, ...state.campaigns] }));
      return created;
    } catch (err) {
      set({ error: toMessage(err, 'Sao chép chiến dịch thất bại') });
      throw err;
    }
  },

  updateStatus: async (id, status) => {
    set({ error: null });
    try {
      const updated = await campaignService.updateStatus(id, status);
      set((state) => ({
        campaigns: state.campaigns.map((c) => (c.id === id ? updated : c)),
      }));
      return updated;
    } catch (err) {
      set({ error: toMessage(err, 'Cập nhật trạng thái thất bại') });
      throw err;
    }
  },

  updateBudget: async (id, input) => {
    set({ error: null });
    try {
      const updated = await campaignService.updateBudget(id, input);
      set((state) => ({
        campaigns: state.campaigns.map((c) => (c.id === id ? updated : c)),
      }));
      return updated;
    } catch (err) {
      set({ error: toMessage(err, 'Cập nhật ngân sách thất bại') });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
